
import {
  FOG_FRAGMENT_GLSL,
  FOG_UNIFORMS,
  FOG_VERTEX_GLSL,
  collectPcBlockers,
  treeShapesFor,
  type Fog,
  type PcBlockerCandidate,
  type TreeCollisionData,
  type TreeShapes,
} from "../camera";
import { PC_BLOCKER_RAMP_SIZE, pcBlockerRampAlpha } from "../pcblockerramp";
import { resolveTreeTexture, treeQuadSizeMm } from "../props";
import type {
  MeshEntry,
  MeshManifest,
  Placement,
  PlacementPosition,
  PlacementsManifest,
  PropDrawItem,
  TextureManifest,
  TreePlacement,
} from "../props";
import type { ViewProjection, WorldBounds } from "../window";
import { buildVisibleProps } from "../props";
import { createBuffer, createVertexArray, describeAttributes } from "./buffer";
import { GlError, checkGlError } from "./context";
import { createProgram, uniformLocations, type Uniforms } from "./shader";
import { SUN_DIR_GLSL } from "./sun";
import { loadImage } from "./texture";
import { dropPose } from "../droppose";
import { creatureModelMatrix } from "../creaturemesh";

/** Bytes per vertex record: position.xyz, normal.xyz, uv.xy, all float32.
 *  Matches `meshes/index.json`'s own declared `vertexStride` -- checked at
 *  load rather than assumed, the same discipline `tiles.ts` applies to its
 *  own binary header. */
const VERTEX_STRIDE = 32;
const OFFSET_POSITION = 0;
const OFFSET_NORMAL = 12;
const OFFSET_UV = 24;

const PROP_AMBIENT = 0.55;

const BUILDING_VERTEX_SOURCE = `#version 300 es
precision highp float;

in vec3 a_position;
in vec3 a_normal;
in vec2 a_uv;

uniform mat4 u_model;
uniform mat4 u_viewProj;

out vec2 v_uv;
out vec3 v_normal;

${FOG_VERTEX_GLSL}

void main() {
  vec4 world = u_model * vec4(a_position, 1.0);
  gl_Position = u_viewProj * world;
  v_uv = a_uv;
  // The vertex's footprint on the ground, not how far it is from the eye: a
  // roof and the doorstep below it are the same distance into the fog, and
  // anything measured from the eye would say otherwise. See camera.ts.
  v_fog = fogFactor(world.xy);
  // The model matrix is rotation and translation only -- see props.ts -- so
  // its own upper-left 3x3 carries a normal exactly as well as an inverse
  // transpose would, without computing one every draw for a scale that never
  // happens.
  v_normal = mat3(u_model) * a_normal;
}
`;

const BUILDING_FRAGMENT_SOURCE = `#version 300 es
precision highp float;

in vec2 v_uv;
in vec3 v_normal;

uniform sampler2D u_texture;

out vec4 fragColour;

const vec3 SUN_DIR = ${SUN_DIR_GLSL};
const float AMBIENT = ${PROP_AMBIENT};

${FOG_FRAGMENT_GLSL}

void main() {
  vec3 normal = normalize(v_normal);
  float lit = AMBIENT + (1.0 - AMBIENT) * max(dot(normal, SUN_DIR), 0.0);
  vec4 texel = texture(u_texture, v_uv);
  fragColour = vec4(applyFog(texel.rgb * lit), texel.a);
}
`;

const BUILDING_UNIFORMS = ["u_model", "u_viewProj", "u_texture", ...FOG_UNIFORMS] as const;

/** The same building, re-drawn because it stands between the camera and the
 *  player. `RenderPCBlocker` (`GameLib/MapOutdoorRender.cpp:611-660`) keeps
 *  stage 0's colour and takes alpha from `PCBlockerAlpha.dds` alone, indexed by
 *  `D3DTSS_TCI_CAMERASPACEPOSITION` through `m_matBuildingTransparent`
 *  (`GameLib/MapOutdoorLoad.cpp:439-441`). */
const BLOCKER_VERTEX_SOURCE = BUILDING_VERTEX_SOURCE.replace(
  "out vec3 v_normal;",
  `out vec3 v_normal;
out vec2 v_ramp;

uniform mat4 u_view;
uniform vec2 u_rampScale;`,
).replace(
  "v_normal = mat3(u_model) * a_normal;",
  `v_normal = mat3(u_model) * a_normal;
  // The first two camera-space components only: the original's texture
  // transform is D3DTTFF_COUNT2, so the depth down the view axis never
  // reaches the ramp and the hole is a cylinder around that axis.
  v_ramp = (u_view * world).xy * u_rampScale + 0.5;`,
);

const BLOCKER_FRAGMENT_SOURCE = BUILDING_FRAGMENT_SOURCE.replace(
  "uniform sampler2D u_texture;",
  `uniform sampler2D u_texture;
uniform sampler2D u_ramp;

in vec2 v_ramp;`,
).replace(
  "fragColour = vec4(applyFog(texel.rgb * lit), texel.a);",
  // Colour from the pass below, alpha from the ramp: stage 1 is
  // COLOROP SELECTARG1/D3DTA_CURRENT and ALPHAOP SELECTARG1/D3DTA_TEXTURE
  // (`MapOutdoorRender.cpp:628-631`).
  "fragColour = vec4(applyFog(texel.rgb * lit), texture(u_ramp, v_ramp).r);",
);

const BLOCKER_UNIFORMS = [...BUILDING_UNIFORMS, "u_view", "u_rampScale", "u_ramp"] as const;

/** One 2014 client unit in this tree's millimetres. `CAMERA_MAX_DISTANCE` is
 *  2500 there (`UserInterface/CameraProcedure.cpp:11`) and 25,000 here
 *  (`camera.ts`), so the ramp's screen-pixel divisor is in units and the
 *  position it divides is in millimetres. */
const LEGACY_UNIT_MM = 10;

/** Where the camera and the player are this frame, for the blocker sweep. */
export interface PcBlockerFrame {
  readonly eyeMm: readonly [number, number, number];
  readonly targetMm: readonly [number, number, number];
  readonly playerMm: readonly [number, number, number];
  readonly distanceMm: number;
  /** Both matrices: the projection the pass draws with, and the eye's own
   *  frame the ramp is indexed in. */
  readonly view: { readonly viewProj: Float32Array; readonly view: Float32Array };
  readonly widthPx: number;
  readonly heightPx: number;
}

/** A GL type for `meshes/index.json`'s own `indexType` string. */
function glIndexType(gl: WebGL2RenderingContext, indexType: MeshEntry["indexType"]): number {
  return indexType === "uint32" ? gl.UNSIGNED_INT : gl.UNSIGNED_SHORT;
}

/**
 * The buildings: one mega VBO/IBO, one VAO whose attribute offsets are
 * rewritten before every draw, one texture per placement's own mesh.
 */
export class GlBuildings {
  private readonly program: WebGLProgram;
  private readonly uniforms: Uniforms<(typeof BUILDING_UNIFORMS)[number]>;
  private readonly vao: WebGLVertexArrayObject;
  private readonly fallbackTexture: WebGLTexture;
  /** Looked up once rather than by name in the draw loop: `getAttribLocation`
   *  is a string lookup into the driver, and this loop already runs it three
   *  times per visible mesh, every frame. */
  private readonly attribPosition: number;
  private readonly attribNormal: number;
  private readonly attribUv: number;
  /** Textures already reported missing, so a building whose skin failed to
   *  load warns once at boot rather than once a frame for the rest of the
   *  session. */
  private readonly warnedMissingTextures = new Set<string>();
  private warnedMissingModels = false;
  private readonly vbo: WebGLBuffer;
  private readonly blockerProgram: WebGLProgram;
  private readonly blockerUniforms: Uniforms<(typeof BLOCKER_UNIFORMS)[number]>;
  private readonly blockerVao: WebGLVertexArrayObject;
  private readonly rampTexture: WebGLTexture;
  /** What `draw` held back this frame, furthest from the eye first, for
   *  `drawBlockers` to put back translucently. */
  private blockers: readonly PropDrawItem[] = [];

  constructor(
    private readonly gl: WebGL2RenderingContext,
    meshBlob: ArrayBuffer,
    private readonly models: MeshManifest,
    private readonly textures: ReadonlyMap<string, WebGLTexture>,
    private readonly placements: readonly Placement[],
  ) {
    if (models.vertexStride !== VERTEX_STRIDE) {
      throw new GlError(
        `meshes/index.json declares a ${models.vertexStride}-byte vertex, this shader reads ${VERTEX_STRIDE}`,
      );
    }

    this.program = createProgram(gl, BUILDING_VERTEX_SOURCE, BUILDING_FRAGMENT_SOURCE, "buildings");
    this.uniforms = uniformLocations(gl, this.program, BUILDING_UNIFORMS);

    const vbo = createBuffer(gl, "buildings mega-VBO");
    this.vbo = vbo;
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, meshBlob, gl.STATIC_DRAW);

    const ibo = createBuffer(gl, "buildings mega-IBO");
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    // The identical bytes, bound the other way -- see this file's module doc
    // on why one blob serves both bindings.
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, meshBlob, gl.STATIC_DRAW);

    this.vao = createVertexArray(gl, "buildings");
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    // Offsets are placeholders here -- `draw` overwrites them per mesh before
    // every `drawElements` call, since every model's slice of the shared VBO
    // starts at a different byte.
    describeAttributes(gl, this.program, VERTEX_STRIDE, [
      { name: "a_position", components: 3, type: gl.FLOAT, normalized: false, offset: OFFSET_POSITION, divisor: 0 },
      { name: "a_normal", components: 3, type: gl.FLOAT, normalized: false, offset: OFFSET_NORMAL, divisor: 0 },
      { name: "a_uv", components: 2, type: gl.FLOAT, normalized: false, offset: OFFSET_UV, divisor: 0 },
    ]);
    gl.bindVertexArray(null);

    this.attribPosition = gl.getAttribLocation(this.program, "a_position");
    this.attribNormal = gl.getAttribLocation(this.program, "a_normal");
    this.attribUv = gl.getAttribLocation(this.program, "a_uv");

    this.blockerProgram = createProgram(
      gl, BLOCKER_VERTEX_SOURCE, BLOCKER_FRAGMENT_SOURCE, "pc blockers");
    this.blockerUniforms = uniformLocations(gl, this.blockerProgram, BLOCKER_UNIFORMS);
    this.blockerVao = createVertexArray(gl, "pc blockers");
    gl.bindVertexArray(this.blockerVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    describeAttributes(gl, this.blockerProgram, VERTEX_STRIDE, [
      { name: "a_position", components: 3, type: gl.FLOAT, normalized: false, offset: OFFSET_POSITION, divisor: 0 },
      { name: "a_normal", components: 3, type: gl.FLOAT, normalized: false, offset: OFFSET_NORMAL, divisor: 0 },
      { name: "a_uv", components: 2, type: gl.FLOAT, normalized: false, offset: OFFSET_UV, divisor: 0 },
    ]);
    gl.bindVertexArray(null);
    this.rampTexture = createRampTexture(gl);

    this.fallbackTexture = createFallbackTexture(gl, [0.5, 0.5, 0.5]);
    checkGlError(gl, "buildings mega-buffer upload");
  }

  draw(
    view: ViewProjection,
    bounds: WorldBounds,
    eye: PlacementPosition,
    fog: Fog,
    blocker: PcBlockerFrame | null = null,
    target: { readonly xMm: number; readonly yMm: number } = eye,
  ): void {
    const { items, missingModels } = buildVisibleProps(this.placements, this.models, bounds, eye, target);
    if (missingModels > 0 && !this.warnedMissingModels) {
      this.warnedMissingModels = true;
      console.warn(
        `${missingModels} building placement(s) name a model missing from meshes/index.json`,
      );
    }
    // `FPCBlockerHide` takes every blocker out of this pass
    // (`GameLib/MapOutdoorRender.cpp:402`); `drawBlockers` puts them back.
    const held = blocker === null ? [] : collectPcBlockers(
      items as readonly PcBlockerCandidate[],
      blocker.eyeMm, blocker.targetMm, blocker.playerMm, blocker.distanceMm,
    );
    this.blockers = held.map((index) => items[index]);
    const hidden = new Set(held);
    if (items.length === 0) return;

    const gl = this.gl;
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    // Left on by the terrain pass already, but set explicitly for the same
    // reason terrain.ts does: this draw earns its own depth state rather than
    // trusting whatever ran immediately before it in the frame.
    gl.enable(gl.DEPTH_TEST);
    gl.uniformMatrix4fv(this.uniforms.u_viewProj, false, view.viewProj);
    setFogUniforms(gl, this.uniforms, fog);
    gl.activeTexture(gl.TEXTURE0);
    gl.uniform1i(this.uniforms.u_texture, 0);

    for (const [index, item] of items.entries()) {
      if (hidden.has(index)) continue;
      gl.uniformMatrix4fv(this.uniforms.u_model, false, item.model);
      for (const mesh of item.meshes) {
        const texture = this.textures.get(mesh.texture);
        if (texture === undefined && !this.warnedMissingTextures.has(mesh.texture)) {
          this.warnedMissingTextures.add(mesh.texture);
          console.warn(`building texture unavailable, drawing grey: ${mesh.texture}`);
        }
        gl.bindTexture(gl.TEXTURE_2D, texture ?? this.fallbackTexture);

        gl.vertexAttribPointer(
          this.attribPosition,
          3,
          gl.FLOAT,
          false,
          VERTEX_STRIDE,
          mesh.vertexOffset + OFFSET_POSITION,
        );
        gl.vertexAttribPointer(this.attribNormal, 3, gl.FLOAT, false, VERTEX_STRIDE, mesh.vertexOffset + OFFSET_NORMAL);
        gl.vertexAttribPointer(this.attribUv, 2, gl.FLOAT, false, VERTEX_STRIDE, mesh.vertexOffset + OFFSET_UV);

        gl.drawElements(gl.TRIANGLES, mesh.indexCount, glIndexType(gl, mesh.indexType), mesh.indexOffset);
      }
    }

    gl.bindVertexArray(null);
  }

  /** What `draw` held back, blended over the frame the character is already
   *  in. `RenderPCBlocker` runs after `m_kChrMgr.Render()`
   *  (`UserInterface/PythonApplication.cpp:198,212`), which is what makes the
   *  character visible through the wall rather than behind it. */
  drawBlockers(fog: Fog, blocker: PcBlockerFrame): void {
    if (this.blockers.length === 0) return;

    const gl = this.gl;
    gl.useProgram(this.blockerProgram);
    gl.bindVertexArray(this.blockerVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    // `StateManager.cpp:205-206`, the blend the original never changes here.
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.uniformMatrix4fv(this.blockerUniforms.u_viewProj, false, blocker.view.viewProj);
    gl.uniformMatrix4fv(this.blockerUniforms.u_view, false, blocker.view.view);
    gl.uniform2f(
      this.blockerUniforms.u_rampScale,
      1 / (blocker.widthPx * LEGACY_UNIT_MM),
      -1 / (blocker.heightPx * LEGACY_UNIT_MM),
    );
    setFogUniforms(gl, this.blockerUniforms, fog);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.rampTexture);
    gl.uniform1i(this.blockerUniforms.u_ramp, 1);
    gl.activeTexture(gl.TEXTURE0);
    gl.uniform1i(this.blockerUniforms.u_texture, 0);

    for (const item of this.blockers) {
      gl.uniformMatrix4fv(this.blockerUniforms.u_model, false, item.model);
      for (const mesh of item.meshes) {
        gl.bindTexture(gl.TEXTURE_2D, this.textures.get(mesh.texture) ?? this.fallbackTexture);
        gl.vertexAttribPointer(
          this.attribPosition, 3, gl.FLOAT, false, VERTEX_STRIDE, mesh.vertexOffset + OFFSET_POSITION);
        gl.vertexAttribPointer(
          this.attribNormal, 3, gl.FLOAT, false, VERTEX_STRIDE, mesh.vertexOffset + OFFSET_NORMAL);
        gl.vertexAttribPointer(
          this.attribUv, 2, gl.FLOAT, false, VERTEX_STRIDE, mesh.vertexOffset + OFFSET_UV);
        gl.drawElements(gl.TRIANGLES, mesh.indexCount, glIndexType(gl, mesh.indexType), mesh.indexOffset);
      }
    }

    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
  }
}

/** The shipped ramp as a single-channel texture, clamped: the original samples
 *  it with `D3DTADDRESS_CLAMP` on both axes
 *  (`GameLib/MapOutdoorRender.cpp:633-634`), which is what keeps a building
 *  well off the view axis fully opaque instead of wrapping back to the hole. */
function createRampTexture(gl: WebGL2RenderingContext): WebGLTexture {
  const texture = gl.createTexture();
  if (texture === null) throw new GlError("could not create the PC-blocker ramp texture");
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  gl.texImage2D(
    gl.TEXTURE_2D, 0, gl.R8, PC_BLOCKER_RAMP_SIZE, PC_BLOCKER_RAMP_SIZE, 0,
    gl.RED, gl.UNSIGNED_BYTE, pcBlockerRampAlpha(),
  );
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  checkGlError(gl, "PC-blocker ramp upload");
  return texture;
}

const TREE_VERTEX_SOURCE = `#version 300 es
precision highp float;

// (s, t): s in [-0.5, 0.5] across the billboard, t in [0, 1] from the trunk's
// base to the top of the canopy.
in vec2 a_corner;

uniform mat4 u_viewProj;
uniform vec3 u_position;
uniform vec3 u_right;
uniform float u_widthMm;
uniform float u_heightMm;

out vec2 v_uv;

${FOG_VERTEX_GLSL}

void main() {
  // Cylindrical, not spherical: u_right is the camera's own rightward axis
  // (horizontal by construction -- see View.billboardRight) and the vertical
  // offset is always world +z, never the camera's tilted up-vector. A
  // spherical billboard would lean the trunk with every change of pitch,
  // which reads as the tree falling over rather than as a flat cutout turning
  // to face the viewer.
  vec3 world = u_position + u_right * (a_corner.x * u_widthMm) + vec3(0.0, 0.0, a_corner.y * u_heightMm);
  gl_Position = u_viewProj * vec4(world, 1.0);
  v_uv = vec2(a_corner.x + 0.5, 1.0 - a_corner.y);
  // The trunk's own footprint, not the corner's: a billboard is a flat stand-in
  // for a round tree, and fogging its two vertical edges by different amounts
  // would make the cutout read as a leaning sheet rather than as a tree
  // standing in one place.
  v_fog = fogFactor(u_position.xy);
}
`;

const TREE_FRAGMENT_SOURCE = `#version 300 es
precision highp float;

in vec2 v_uv;

uniform sampler2D u_texture;

out vec4 fragColour;

${FOG_FRAGMENT_GLSL}

void main() {
  vec4 texel = texture(u_texture, v_uv);
  // Alpha test, not blending. Three quarters of a tree card is empty, and the
  // browser hands us the PNG premultiplied, so alpha 0 arrives as pure black --
  // without this, every tree is a large opaque black parallelogram with the
  // right foliage painted inside it, which is exactly what shipped the moment
  // the cards resolved. discard rather than GL_BLEND because blending a cutout
  // is only correct back-to-front, and sorting 368 billboards against each
  // other and against every building every frame buys nothing here: foliage
  // alpha is very nearly binary (74% of this card is a == 0, 24% is a == 1, and
  // barely 2% lies in between), which is the case discard exists for. It also
  // keeps the depth buffer honest, so a tree in front of a wall hides the wall
  // rather than tinting it.
  if (texel.a < 0.5) discard;
  // After the discard, so a fogged-out tree fades to the void colour as a tree
  // silhouette and never as a rectangle: the fragments that would have made the
  // card visible are already gone by the time anything is mixed.
  fragColour = vec4(applyFog(texel.rgb), 1.0);
}
`;

const TREE_UNIFORMS = [
  "u_viewProj",
  "u_position",
  "u_right",
  "u_widthMm",
  "u_heightMm",
  "u_texture",
  ...FOG_UNIFORMS,
] as const;

/** A tree re-drawn because it stands between the camera and the player.
 *  `OnRenderPCBlocker` modulates the texture's alpha by the ramp on stage 1
 *  (`SpeedTreeLib/SpeedTreeWrapper.cpp:91-93`) with blending on (`:105`), the
 *  ramp indexed as the buildings' is (`GameLib/MapOutdoorRender.cpp:627-637`). */
const TREE_BLOCKER_VERTEX_SOURCE = TREE_VERTEX_SOURCE.replace(
  "out vec2 v_uv;",
  `out vec2 v_uv;
out vec2 v_ramp;

uniform mat4 u_view;
uniform vec2 u_rampScale;`,
).replace(
  "gl_Position = u_viewProj * vec4(world, 1.0);",
  `gl_Position = u_viewProj * vec4(world, 1.0);
  v_ramp = (u_view * vec4(world, 1.0)).xy * u_rampScale + 0.5;`,
);

const TREE_BLOCKER_FRAGMENT_SOURCE = TREE_FRAGMENT_SOURCE.replace(
  "uniform sampler2D u_texture;",
  `uniform sampler2D u_texture;
uniform sampler2D u_ramp;

in vec2 v_ramp;`,
).replace(
  "fragColour = vec4(applyFog(texel.rgb), 1.0);",
  "fragColour = vec4(applyFog(texel.rgb), texel.a * texture(u_ramp, v_ramp).r);",
);

const TREE_BLOCKER_UNIFORMS = [...TREE_UNIFORMS, "u_view", "u_rampScale", "u_ramp"] as const;

/** One quad, base at t=0, canopy at t=1, as a triangle strip. */
const TREE_QUAD = new Float32Array([-0.5, 0, 0.5, 0, -0.5, 1, 0.5, 1]);

/** A tree placement with its texture already resolved -- built once, not
 *  every frame, since `resolveTreeTexture` and the manifest lookup it does
 *  are the same answer every time for a given map. */
interface TreeItem {
  readonly placement: TreePlacement;
  readonly textureKey: string;
  /** Its index in the map's placed trees, the order `treeShapes` counts in. */
  readonly index: number;
}

export class GlTrees {
  private readonly program: WebGLProgram;
  private readonly uniforms: Uniforms<(typeof TREE_UNIFORMS)[number]>;
  private readonly vao: WebGLVertexArrayObject;
  private readonly items: readonly TreeItem[];
  private readonly blockerProgram: WebGLProgram;
  private readonly blockerUniforms: Uniforms<(typeof TREE_BLOCKER_UNIFORMS)[number]>;
  private readonly rampTexture: WebGLTexture;
  /** Each placed tree's collision objects, by placement index; empty until
   *  `setShapes`, and a tree with none never blocks. */
  private shapes: readonly TreeShapes[] = [];
  /** What `draw` held back this frame, furthest from the eye first. */
  private blockers: readonly TreeItem[] = [];

  constructor(
    private readonly gl: WebGL2RenderingContext,
    private readonly textures: ReadonlyMap<string, WebGLTexture>,
    private readonly placements: readonly TreePlacement[],
    textureManifest: TextureManifest,
  ) {
    this.program = createProgram(gl, TREE_VERTEX_SOURCE, TREE_FRAGMENT_SOURCE, "trees");
    this.uniforms = uniformLocations(gl, this.program, TREE_UNIFORMS);
    this.blockerProgram = createProgram(gl, TREE_BLOCKER_VERTEX_SOURCE, TREE_BLOCKER_FRAGMENT_SOURCE, "tree blockers");
    this.blockerUniforms = uniformLocations(gl, this.blockerProgram, TREE_BLOCKER_UNIFORMS);
    this.rampTexture = createRampTexture(gl);

    const items: TreeItem[] = [];
    for (const [index, placement] of placements.entries()) {
      const textureKey = resolveTreeTexture(placement.model, textureManifest);
      if (textureKey !== null) items.push({ placement, textureKey, index });
    }
    this.items = items;

    const vbo = createBuffer(gl, "tree billboard quad");
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, TREE_QUAD, gl.STATIC_DRAW);

    this.vao = createVertexArray(gl, "trees");
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    describeAttributes(gl, this.program, 8, [
      { name: "a_corner", components: 2, type: gl.FLOAT, normalized: false, offset: 0, divisor: 0 },
    ]);
    gl.bindVertexArray(null);
    checkGlError(gl, "tree billboard upload");
  }

  get resolvedCount(): number {
    return this.items.length;
  }

  /** The map's tree collision file, cut into each placed tree's objects. */
  setShapes(data: TreeCollisionData): void {
    this.shapes = treeShapesFor(data, this.placements.map((placement) => placement.position));
  }

  draw(
    view: ViewProjection,
    bounds: WorldBounds,
    right: readonly [number, number, number],
    fog: Fog,
    blocker: PcBlockerFrame | null = null,
  ): void {
    const visible = this.items.filter((item) => {
      const { position } = item.placement;
      const size = treeQuadSizeMm(item.placement.size);
      return withinBounds(position.xMm, position.yMm, Math.max(size.widthMm, size.heightMm) * 0.5, bounds);
    });
    // `m_bTransparentTree` (`GameLib/MapOutdoor.cpp:183`) lets a tree the sweep
    // takes into the blocker list (`MapOutdoorUpdate.cpp:640-641`), which
    // `FPCBlockerHide` takes out of this pass (`MapOutdoorRender.cpp:402`).
    const held = blocker === null ? [] : collectPcBlockers(
      visible.map((item): PcBlockerCandidate => ({
        centreMm: [item.placement.position.xMm, item.placement.position.yMm, item.placement.position.zMm],
        radiusMm: 0,
        isTree: true,
        shapes: this.shapes[item.index] ?? { cylinders: [], spheres: [] },
      })),
      blocker.eyeMm, blocker.targetMm, blocker.playerMm, blocker.distanceMm,
    );
    this.blockers = held.map((index) => visible[index]);
    if (visible.length === 0) return;
    const hidden = new Set(this.blockers);

    const gl = this.gl;
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    gl.enable(gl.DEPTH_TEST);
    gl.uniformMatrix4fv(this.uniforms.u_viewProj, false, view.viewProj);
    gl.uniform3f(this.uniforms.u_right, right[0], right[1], right[2]);
    setFogUniforms(gl, this.uniforms, fog);
    gl.activeTexture(gl.TEXTURE0);
    gl.uniform1i(this.uniforms.u_texture, 0);

    for (const item of visible) {
      if (!hidden.has(item)) this.drawCard(this.uniforms, item);
    }

    gl.bindVertexArray(null);
  }

  /** What `draw` held back, blended over the characters as `RenderPCBlocker`
   *  draws it after them (`UserInterface/PythonApplication.cpp:198,212`). */
  drawBlockers(fog: Fog, blocker: PcBlockerFrame, right: readonly [number, number, number]): void {
    if (this.blockers.length === 0) return;

    const gl = this.gl;
    gl.useProgram(this.blockerProgram);
    gl.bindVertexArray(this.vao);
    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    // `StateManager.cpp:205-206`, the blend the original never changes here.
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.uniformMatrix4fv(this.blockerUniforms.u_viewProj, false, blocker.view.viewProj);
    gl.uniformMatrix4fv(this.blockerUniforms.u_view, false, blocker.view.view);
    gl.uniform2f(
      this.blockerUniforms.u_rampScale,
      1 / (blocker.widthPx * LEGACY_UNIT_MM),
      -1 / (blocker.heightPx * LEGACY_UNIT_MM),
    );
    gl.uniform3f(this.blockerUniforms.u_right, right[0], right[1], right[2]);
    setFogUniforms(gl, this.blockerUniforms, fog);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.rampTexture);
    gl.uniform1i(this.blockerUniforms.u_ramp, 1);
    gl.activeTexture(gl.TEXTURE0);
    gl.uniform1i(this.blockerUniforms.u_texture, 0);

    for (const item of this.blockers) this.drawCard(this.blockerUniforms, item);

    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
  }

  private drawCard(uniforms: Uniforms<(typeof TREE_UNIFORMS)[number]>, item: TreeItem): void {
    const gl = this.gl;
    const texture = this.textures.get(item.textureKey);
    if (texture === undefined) return; // loaded manifest entry, failed fetch -- skip rather than draw grey foliage
    const { position } = item.placement;
    const size = treeQuadSizeMm(item.placement.size);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.uniform3f(uniforms.u_position, position.xMm, position.yMm, position.zMm);
    gl.uniform1f(uniforms.u_widthMm, size.widthMm);
    gl.uniform1f(uniforms.u_heightMm, size.heightMm);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
}

/** The three uniforms the fog snippets declare, set once per pass. A function rather
 *  than three lines in each `draw`, so the buildings and the trees cannot end up
 *  fading on different schedules from each other or from the ground. */
function setFogUniforms(
  gl: WebGL2RenderingContext,
  uniforms: Uniforms<(typeof FOG_UNIFORMS)[number]>,
  fog: Fog,
): void {
  gl.uniform2f(uniforms.u_fogCameraMm, fog.cameraXMm, fog.cameraYMm);
  gl.uniform2f(uniforms.u_fogRangeMm, fog.nearMm, fog.farMm);
  gl.uniform3f(uniforms.u_fogColour, fog.colour[0], fog.colour[1], fog.colour[2]);
}

function withinBounds(xMm: number, yMm: number, radiusMm: number, bounds: WorldBounds): boolean {
  const nearestX = Math.min(Math.max(xMm, bounds.minXMm), bounds.maxXMm);
  const nearestY = Math.min(Math.max(yMm, bounds.minYMm), bounds.maxYMm);
  const dx = xMm - nearestX;
  const dy = yMm - nearestY;
  return dx * dx + dy * dy <= radiusMm * radiusMm;
}

/** `dropmodels/index.json`, as `tools/build_drop_models.py` writes it. */
export interface DropModelIndex extends MeshManifest {
  /** Vnum to the model its `item_list.txt` row names. */
  readonly items: Readonly<Record<string, string>>;
  /** `item_bag.gr2`, the drop model of a row that names none (`ItemData.cpp:162-163`). */
  readonly default: string;
  /** Models the converter could not decode, which have no meshes here. */
  readonly unconverted: readonly string[];
  /** Skin path to its file under `dropmodels/textures/`. */
  readonly textures: Readonly<Record<string, string>>;
}

/** One ground item the frame draws, and when this client first saw it land. */
export interface LyingItem {
  readonly id: number;
  readonly vnum: number;
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
  readonly seenMs: number;
}

/** A quaternion and a position as a column-major model matrix. */
function poseMatrix(q: readonly [number, number, number, number], at: readonly [number, number, number]): Float32Array {
  const [x, y, z, w] = q;
  return new Float32Array([
    1 - 2 * (y * y + z * z), 2 * (x * y + z * w), 2 * (x * z - y * w), 0,
    2 * (x * y - z * w), 1 - 2 * (x * x + z * z), 2 * (y * z + x * w), 0,
    2 * (x * z + y * w), 2 * (y * z - x * w), 1 - 2 * (x * x + y * y), 0,
    at[0], at[1], at[2], 1,
  ]);
}

/**
 * The ground items, each in its drop model: `CPythonItem::Render` draws every
 * ground instance's `ThingInstance` (`PythonItem.cpp:121-132`), built from
 * `GetDropModelThing` (`:294-321`) and turned by `TGroundItemInstance::Update`.
 */
export class GlDropItems {
  private readonly program: WebGLProgram;
  private readonly uniforms: Uniforms<(typeof BUILDING_UNIFORMS)[number]>;
  private readonly vao: WebGLVertexArrayObject;
  private readonly vbo: WebGLBuffer;
  private readonly fallbackTexture: WebGLTexture;
  private readonly attribPosition: number;
  private readonly attribNormal: number;
  private readonly attribUv: number;
  private readonly unconverted: ReadonlySet<string>;

  constructor(
    private readonly gl: WebGL2RenderingContext,
    blob: ArrayBuffer,
    private readonly index: DropModelIndex,
    private readonly textures: ReadonlyMap<string, WebGLTexture>,
  ) {
    this.program = createProgram(gl, BUILDING_VERTEX_SOURCE, BUILDING_FRAGMENT_SOURCE, "drop items");
    this.uniforms = uniformLocations(gl, this.program, BUILDING_UNIFORMS);
    this.vbo = createBuffer(gl, "drop items VBO");
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, blob, gl.STATIC_DRAW);
    const ibo = createBuffer(gl, "drop items IBO");
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, blob, gl.STATIC_DRAW);
    this.vao = createVertexArray(gl, "drop items");
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    describeAttributes(gl, this.program, VERTEX_STRIDE, [
      { name: "a_position", components: 3, type: gl.FLOAT, normalized: false, offset: OFFSET_POSITION, divisor: 0 },
      { name: "a_normal", components: 3, type: gl.FLOAT, normalized: false, offset: OFFSET_NORMAL, divisor: 0 },
      { name: "a_uv", components: 2, type: gl.FLOAT, normalized: false, offset: OFFSET_UV, divisor: 0 },
    ]);
    gl.bindVertexArray(null);
    this.attribPosition = gl.getAttribLocation(this.program, "a_position");
    this.attribNormal = gl.getAttribLocation(this.program, "a_normal");
    this.attribUv = gl.getAttribLocation(this.program, "a_uv");
    this.fallbackTexture = createFallbackTexture(gl, [0.5, 0.5, 0.5]);
    this.unconverted = new Set(index.unconverted);
    checkGlError(gl, "drop items upload");
  }

  /** The drop model `vnum` lies in: its row's model, the bag for a row that
   *  names none (`ItemData.cpp:154-165`), or null when that model has no mesh here. */
  modelFor(vnum: number): string | null {
    const model = this.index.items[String(vnum)] ?? this.index.default;
    return this.unconverted.has(model) || this.index.models[model] === undefined ? null : model;
  }

  draw(view: ViewProjection, fog: Fog, items: readonly LyingItem[], nowMs: number): void {
    if (items.length === 0) return;
    const gl = this.gl;
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.enable(gl.DEPTH_TEST);
    gl.uniformMatrix4fv(this.uniforms.u_viewProj, false, view.viewProj);
    setFogUniforms(gl, this.uniforms, fog);
    gl.activeTexture(gl.TEXTURE0);
    gl.uniform1i(this.uniforms.u_texture, 0);

    for (const item of items) {
      const key = this.modelFor(item.vnum);
      if (key === null) continue;
      const meshes = this.index.models[key].meshes;
      const min: [number, number, number] = [Infinity, Infinity, Infinity];
      const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
      for (const mesh of meshes) {
        for (let axis = 0; axis < 3; axis += 1) {
          min[axis] = Math.min(min[axis], mesh.bboxMin[axis]);
          max[axis] = Math.max(max[axis], mesh.bboxMax[axis]);
        }
      }
      // `GetBoundBox` after the model is placed at the origin (`PythonItem.cpp:322-325`).
      const pose = dropPose(min, max, item.id, [item.xMm, item.yMm, item.zMm], nowMs - item.seenMs);
      gl.uniformMatrix4fv(this.uniforms.u_model, false, poseMatrix(pose.rotation, pose.positionMm));
      for (const mesh of meshes) {
        gl.bindTexture(gl.TEXTURE_2D, this.textures.get(mesh.texture) ?? this.fallbackTexture);
        gl.vertexAttribPointer(this.attribPosition, 3, gl.FLOAT, false, VERTEX_STRIDE, mesh.vertexOffset + OFFSET_POSITION);
        gl.vertexAttribPointer(this.attribNormal, 3, gl.FLOAT, false, VERTEX_STRIDE, mesh.vertexOffset + OFFSET_NORMAL);
        gl.vertexAttribPointer(this.attribUv, 2, gl.FLOAT, false, VERTEX_STRIDE, mesh.vertexOffset + OFFSET_UV);
        gl.drawElements(gl.TRIANGLES, mesh.indexCount, glIndexType(gl, mesh.indexType), mesh.indexOffset);
      }
    }
    gl.bindVertexArray(null);
  }
}

/** `guildsymbol/index.json`, as `tools/build_guild_symbol_model.py` writes it. */
export interface GuildSymbolIndex extends MeshManifest {
  /** `guild_symbol.msm`'s `BaseModelFileName`. */
  readonly model: string;
  /** The first shape's first skin, the one `ChangeMaterial` replaces. */
  readonly skin: string;
  /** Skin path to its file under `guildsymbol/textures/`. */
  readonly textures: Readonly<Record<string, string>>;
}

/** One guild insignia this frame draws. */
export interface PlacedInsignia {
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
  readonly heading: number;
  readonly guildId: number;
}

/**
 * Race 14200 in its own model, wearing its guild's symbol: `CInstanceBase::Create`
 * calls `ChangeMaterial(GetGuildSymbolFileName(guild))` when that file exists
 * (`InstanceBase.cpp:832-839`), and `ChangeMaterial` hands the image to
 * `SetMaterialImagePointer` for the first skin of the body's shape
 * (`ActorInstanceData.cpp:271-296`).
 */
export class GlGuildSymbols {
  private readonly program: WebGLProgram;
  private readonly uniforms: Uniforms<(typeof BUILDING_UNIFORMS)[number]>;
  private readonly vao: WebGLVertexArrayObject;
  private readonly vbo: WebGLBuffer;
  private readonly fallbackTexture: WebGLTexture;
  private readonly attribPosition: number;
  private readonly attribNormal: number;
  private readonly attribUv: number;

  constructor(
    private readonly gl: WebGL2RenderingContext,
    blob: ArrayBuffer,
    private readonly index: GuildSymbolIndex,
    private readonly textures: ReadonlyMap<string, WebGLTexture>,
  ) {
    this.program = createProgram(gl, BUILDING_VERTEX_SOURCE, BUILDING_FRAGMENT_SOURCE, "guild insignia");
    this.uniforms = uniformLocations(gl, this.program, BUILDING_UNIFORMS);
    this.vbo = createBuffer(gl, "guild insignia VBO");
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, blob, gl.STATIC_DRAW);
    const ibo = createBuffer(gl, "guild insignia IBO");
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, blob, gl.STATIC_DRAW);
    this.vao = createVertexArray(gl, "guild insignia");
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    describeAttributes(gl, this.program, VERTEX_STRIDE, [
      { name: "a_position", components: 3, type: gl.FLOAT, normalized: false, offset: OFFSET_POSITION, divisor: 0 },
      { name: "a_normal", components: 3, type: gl.FLOAT, normalized: false, offset: OFFSET_NORMAL, divisor: 0 },
      { name: "a_uv", components: 2, type: gl.FLOAT, normalized: false, offset: OFFSET_UV, divisor: 0 },
    ]);
    gl.bindVertexArray(null);
    this.attribPosition = gl.getAttribLocation(this.program, "a_position");
    this.attribNormal = gl.getAttribLocation(this.program, "a_normal");
    this.attribUv = gl.getAttribLocation(this.program, "a_uv");
    this.fallbackTexture = createFallbackTexture(gl, [0.5, 0.5, 0.5]);
    checkGlError(gl, "guild insignia upload");
  }

  /** Each insignia in its model; `symbolFor` answers a guild's uploaded symbol
   *  texture, or null when it has none and the model keeps its own skin. */
  draw(
    view: ViewProjection,
    fog: Fog,
    insignias: readonly PlacedInsignia[],
    symbolFor: (guildId: number) => WebGLTexture | null,
  ): void {
    const model = this.index.models[this.index.model];
    if (insignias.length === 0 || model === undefined) return;
    const gl = this.gl;
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.enable(gl.DEPTH_TEST);
    gl.uniformMatrix4fv(this.uniforms.u_viewProj, false, view.viewProj);
    setFogUniforms(gl, this.uniforms, fog);
    gl.activeTexture(gl.TEXTURE0);
    gl.uniform1i(this.uniforms.u_texture, 0);

    for (const insignia of insignias) {
      gl.uniformMatrix4fv(this.uniforms.u_model, false,
        creatureModelMatrix(insignia.xMm, insignia.yMm, insignia.zMm, insignia.heading));
      const symbol = symbolFor(insignia.guildId);
      for (const mesh of model.meshes) {
        const own = this.textures.get(mesh.texture) ?? this.fallbackTexture;
        // `SetMaterialImagePointer(part, m_stSrcFileName, image)` replaces only the first skin (`ActorInstanceData.cpp:283-295`).
        gl.bindTexture(gl.TEXTURE_2D, mesh.texture === this.index.skin && symbol !== null ? symbol : own);
        gl.vertexAttribPointer(this.attribPosition, 3, gl.FLOAT, false, VERTEX_STRIDE, mesh.vertexOffset + OFFSET_POSITION);
        gl.vertexAttribPointer(this.attribNormal, 3, gl.FLOAT, false, VERTEX_STRIDE, mesh.vertexOffset + OFFSET_NORMAL);
        gl.vertexAttribPointer(this.attribUv, 2, gl.FLOAT, false, VERTEX_STRIDE, mesh.vertexOffset + OFFSET_UV);
        gl.drawElements(gl.TRIANGLES, mesh.indexCount, glIndexType(gl, mesh.indexType), mesh.indexOffset);
      }
    }
    gl.bindVertexArray(null);
  }
}

/** The insignia's pack and its own skin, fetched and decoded but not uploaded. */
export async function loadGuildSymbolModel(): Promise<{
  readonly index: GuildSymbolIndex;
  readonly blob: ArrayBuffer;
  readonly images: ReadonlyMap<string, ImageBitmap | HTMLImageElement>;
}> {
  const index = await fetchJson<GuildSymbolIndex>("guildsymbol/index.json");
  const response = await fetch("guildsymbol/guildsymbol.bin");
  if (!response.ok) throw new GlError(`guildsymbol/guildsymbol.bin: HTTP ${response.status}`);
  const blob = await response.arrayBuffer();
  const images = new Map<string, ImageBitmap | HTMLImageElement>();
  await Promise.all(Object.entries(index.textures).map(async ([texture, file]) => {
    images.set(texture, await loadImage(`guildsymbol/textures/${file}`));
  }));
  return { index, blob, images };
}

/** The drop-model pack and its skins, fetched and decoded but not uploaded. */
export async function loadDropModels(): Promise<{
  readonly index: DropModelIndex;
  readonly blob: ArrayBuffer;
  readonly images: ReadonlyMap<string, ImageBitmap | HTMLImageElement>;
}> {
  const index = await fetchJson<DropModelIndex>("dropmodels/index.json");
  const response = await fetch("dropmodels/dropmodels.bin");
  if (!response.ok) throw new GlError(`dropmodels/dropmodels.bin: HTTP ${response.status}`);
  const blob = await response.arrayBuffer();
  const images = new Map<string, ImageBitmap | HTMLImageElement>();
  await Promise.all(Object.entries(index.textures).map(async ([texture, file]) => {
    images.set(texture, await loadImage(`dropmodels/textures/${file}`));
  }));
  return { index, blob, images };
}

/** A single flat-coloured texture, for a mesh whose real skin failed to fetch.
 *  Grey rather than magenta-checkerboard: a missing building texture is a
 *  deployment problem to fix, not a shader-debugging aid, and the town should
 *  still read as buildings while it is being fixed. */
function createFallbackTexture(gl: WebGL2RenderingContext, rgb: readonly [number, number, number]): WebGLTexture {
  const texture = gl.createTexture();
  if (texture === null) throw new GlError("could not create the fallback prop texture");
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGB8,
    1,
    1,
    0,
    gl.RGB,
    gl.UNSIGNED_BYTE,
    new Uint8Array([rgb[0] * 255, rgb[1] * 255, rgb[2] * 255]),
  );
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return texture;
}

export function createPropTexture(gl: WebGL2RenderingContext, image: ImageBitmap | HTMLImageElement): WebGLTexture {
  const texture = gl.createTexture();
  if (texture === null) throw new GlError("could not create a prop texture");
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, image);
  gl.generateMipmap(gl.TEXTURE_2D);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
  checkGlError(gl, "prop texture upload");
  return texture;
}

/** Everything `create.ts` needs to hand `GlRenderer`, fetched and decoded but
 *  not yet uploaded -- the same split `loadHeightField` and the atlas/tile
 *  fetches in `create.ts` already keep, so a WebGL2 failure downstream never
 *  has to re-fetch anything. */
export interface PropAssets {
  readonly placements: PlacementsManifest;
  readonly meshManifest: MeshManifest;
  readonly meshBlob: ArrayBuffer;
  readonly textureManifest: TextureManifest;
  readonly images: ReadonlyMap<string, ImageBitmap | HTMLImageElement>;
}

export interface PropUrls {
  readonly placementsUrl: string;
  readonly meshIndexUrl: string;
  readonly meshBlobUrl: string;
  readonly textureIndexUrl: string;
  /** Where the decoded texture files (the manifest's own `file` names) live. */
  readonly textureBaseUrl: string;
}

/** Safari can interrupt one of the prop pack's JSON/blob requests while a tab
 *  is backgrounded. Retry the pack once so a transient failure does not turn
 *  the authored buildings and trees into a terrain-only scene. A persistent
 *  failure still reaches createRenderer's bounded no-props fallback. */
export const PROP_ASSET_FETCH_RETRIES = 1;
export const PROP_ASSET_RETRY_DELAY_MS = 250;

export async function loadPropAssetsWithRetry(
  urls: PropUrls,
  loadAssets: (urls: PropUrls) => Promise<PropAssets> = loadPropAssets,
  wait: (milliseconds: number) => Promise<void> = (milliseconds) =>
    new Promise((resolve) => setTimeout(resolve, milliseconds)),
): Promise<PropAssets> {
  let failure: unknown;
  for (let attempt = 0; attempt <= PROP_ASSET_FETCH_RETRIES; attempt += 1) {
    try {
      return await loadAssets(urls);
    } catch (error) {
      failure = error;
      if (attempt === PROP_ASSET_FETCH_RETRIES) break;
      await wait(PROP_ASSET_RETRY_DELAY_MS);
    }
  }
  throw failure;
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) throw new GlError(`${url}: HTTP ${response.status}`);
  return (await response.json()) as T;
}

/** A backgrounded Safari tab can interrupt one prop skin decode while leaving
 * the terrain context alive. Retry once so a building/tree does not remain on
 * the grey texture fallback for the rest of the session. */
export const PROP_TEXTURE_FETCH_RETRIES = 1;
export const PROP_TEXTURE_RETRY_DELAY_MS = 250;

export async function loadPropTextureWithRetry<T>(
  loadTexture: () => Promise<T>,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<T> {
  let failure: unknown;
  for (let attempt = 0; attempt <= PROP_TEXTURE_FETCH_RETRIES; attempt += 1) {
    try {
      return await loadTexture();
    } catch (error) {
      failure = error;
      if (attempt === PROP_TEXTURE_FETCH_RETRIES) break;
      await wait(PROP_TEXTURE_RETRY_DELAY_MS);
    }
  }
  throw failure;
}

export async function loadPropAssets(urls: PropUrls): Promise<PropAssets> {
  const [placements, meshManifest, meshBlob, textureManifest] = await Promise.all([
    fetchJson<PlacementsManifest>(urls.placementsUrl),
    fetchJson<MeshManifest>(urls.meshIndexUrl),
    fetch(urls.meshBlobUrl).then((response) => {
      if (!response.ok) throw new GlError(`${urls.meshBlobUrl}: HTTP ${response.status}`);
      return response.arrayBuffer();
    }),
    fetchJson<TextureManifest>(urls.textureIndexUrl),
  ]);

  // Every texture a building's own mesh names, plus whichever tree stems
  // happen to resolve -- not the manifest's full 47, in principle, though
  // today buildings alone already reference all of them.
  const wanted = new Set<string>();
  for (const model of Object.values(meshManifest.models)) {
    for (const mesh of model.meshes) wanted.add(mesh.texture);
  }
  for (const tree of placements.trees) {
    const key = resolveTreeTexture(tree.model, textureManifest);
    if (key !== null) wanted.add(key);
  }

  const images = new Map<string, ImageBitmap | HTMLImageElement>();
  const loads = await Promise.allSettled(
    [...wanted].map(async (key) => {
      const entry = textureManifest.textures[key];
      if (entry === undefined) return; // named by a mesh but absent from its own manifest -- nothing to fetch
      const image = await loadPropTextureWithRetry(
        () => loadImage(`${urls.textureBaseUrl}/${entry.file}`),
      );
      images.set(key, image);
    }),
  );
  for (const [index, result] of loads.entries()) {
    if (result.status === "rejected") {
      console.warn(`prop texture unavailable (${String(result.reason)}): ${[...wanted][index]}`);
    }
  }

  return { placements, meshManifest, meshBlob, textureManifest, images };
}
